import type { CampaignStatistics } from '@hprofits/api';
import type { AdGroupId, CampaignId, CreativeGroupId, TargetGroupId } from '@hprofits/shared';
import type { CampaignGroupId } from '@hprofits/shared/types';
import { hasValue, isEmpty } from '@hprofits/utils/fp/optional';
import { computed, MaybeRefOrGetter, Ref, toValue } from 'vue';
import { RouteLocation } from 'vue-router';

import { useCampaignGroupContext } from '@/modules/campaigns/composables/context';
import { IS_TARGET_GROUP_LOCK_ENABLED } from '@/modules/campaigns/constants/featureFlags';
import { useRouteResolverCampaigns } from '@/modules/common/route/useRouteResolverCampaigns';

export type PricingRuleUsageRef = {
  ruleId: CampaignId;
  adGroupId: AdGroupId;
};

export type PricingRuleUsage = PricingRuleUsageRef & {
  adGroupName: string;
  linkRoute: RouteLocation;
};

type UsePricingRuleLockParams = Readonly<{
  campaignGroupId: CampaignGroupId;
}> &
  (
    | Readonly<{
        entityId: MaybeRefOrGetter<TargetGroupId | null>;
        kind: 'target';
      }>
    | Readonly<{
        entityId: MaybeRefOrGetter<CreativeGroupId | null>;
        kind: 'creative';
      }>
  );

type PricingRuleLockKind = 'target' | 'creative';

/**
 * Delete guard shared by page-level and sidebar delete actions, for target and creative
 * groups alike. Not behind `IS_TARGET_GROUP_LOCK_ENABLED`: the backend refuses to delete a
 * group a pricing rule uses (`IN_USE`, #718), so an enabled Delete would only lead to an
 * error (#670).
 */
export const isEntityDeleteLockedByPricingRule = (
  campaignStatistics: CampaignStatistics | null | undefined,
  kind: PricingRuleLockKind,
  entityId: TargetGroupId | CreativeGroupId,
): boolean =>
  (campaignStatistics?.adGroups ?? []).some((adGroup) =>
    adGroup.pricingRules.some((rule) =>
      kind === 'target' ? rule.targetGroupId === entityId : rule.creativeGroupId === entityId,
    ),
  );

/**
 * The single point the `IS_TARGET_GROUP_LOCK_ENABLED` flag switches off: readonly
 * dimensions and the lock `LockAlert` derive from it. Delete does not — it is blocked by
 * any usage (`isEntityDeleteLockedByPricingRule`, or `pricingRuleUsages` on the composable).
 */
export const isEntityLockedByPricingRule = (
  campaignStatistics: CampaignStatistics | null | undefined,
  kind: PricingRuleLockKind,
  entityId: TargetGroupId | CreativeGroupId,
): boolean => {
  // The flag covers target groups only: #816 replaced the target lock with conflict
  // indication, creative groups kept theirs.
  if (kind === 'target' && !IS_TARGET_GROUP_LOCK_ENABLED) {
    return false;
  }
  return isEntityDeleteLockedByPricingRule(campaignStatistics, kind, entityId);
};

const collectPricingRuleUsages = (
  campaignStatistics: CampaignStatistics | null | undefined,
  kind: PricingRuleLockKind,
  entityId: TargetGroupId | CreativeGroupId,
  toLinkRoute: (adGroupId: AdGroupId) => RouteLocation,
): ReadonlyArray<PricingRuleUsage> =>
  (campaignStatistics?.adGroups ?? []).flatMap((adGroup) =>
    adGroup.pricingRules
      .filter((rule) => (kind === 'target' ? rule.targetGroupId : rule.creativeGroupId) === entityId)
      .map((rule) => ({
        ruleId: rule.id,
        adGroupId: adGroup.id,
        adGroupName: adGroup.name,
        linkRoute: toLinkRoute(adGroup.id),
      })),
  );

export type UsePricingRuleLockReturn = Readonly<
  Ref<{
    isLockedByPricingRule: boolean;
    firstPricingRuleRef: PricingRuleUsageRef | null;
    lockedPricingRuleLinkRoute: RouteLocation | null;
    pricingRuleUsages: ReadonlyArray<PricingRuleUsage>;
  }>
>;

/**
 * Whether a target/creative group is referenced by any ad group's pricing rule
 * (and which rule) — drives the edit lock; `pricingRuleUsages`, collected whatever the flag
 * says, drives the delete guard. Reads campaign statistics straight from the group context,
 * so no separate pricing-usage context/composable is needed.
 */
export const usePricingRuleLock = ({
  entityId,
  campaignGroupId,
  kind,
}: UsePricingRuleLockParams): UsePricingRuleLockReturn => {
  // TODO: We can just pass data here instead to avoid context dependency
  const { campaignGroupStatisticsSource } = useCampaignGroupContext();
  const routeResolver = useRouteResolverCampaigns();

  return computed(() => {
    const id = toValue(entityId);
    const campaignStatistics = campaignGroupStatisticsSource.state.data.value;
    if (isEmpty(id)) {
      return {
        isLockedByPricingRule: false,
        firstPricingRuleRef: null,
        lockedPricingRuleLinkRoute: null,
        pricingRuleUsages: [],
      };
    }

    const pricingRuleUsages = collectPricingRuleUsages(campaignStatistics, kind, id, (adGroupId) =>
      routeResolver.adGroup(campaignGroupId, adGroupId),
    );

    const isLockedByPricingRule = isEntityLockedByPricingRule(campaignStatistics, kind, id);
    if (!isLockedByPricingRule) {
      return {
        isLockedByPricingRule: false,
        firstPricingRuleRef: null,
        lockedPricingRuleLinkRoute: null,
        pricingRuleUsages,
      };
    }

    const firstUsage = pricingRuleUsages[0] ?? null;
    const firstPricingRuleRef = hasValue(firstUsage)
      ? { ruleId: firstUsage.ruleId, adGroupId: firstUsage.adGroupId }
      : null;
    const lockedPricingRuleLinkRoute = firstUsage?.linkRoute ?? null;

    return {
      isLockedByPricingRule,
      firstPricingRuleRef,
      lockedPricingRuleLinkRoute,
      pricingRuleUsages,
    };
  });
};
